import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import Select from 'react-select';
import { ReactComponent as PencilIcon } from '../../shared/icons/icon-edit.svg';
import { ReactComponent as TrashIcon } from '../../shared/icons/trashcan-icon.svg';
import { UPDATE_TEAM_CONTACT_FIELD_SCHEMA, DELETE_TEAM_CONTACT_FIELD_SCHEMA } from '../../api/contacts/customFieldsApi';
import ConfirmActionModal from '../confirm-action-modal';
import styles from './CustomFieldManager.module.scss';

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px',
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#999',
  }),
};

interface TeamContactFieldSchema {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: string;
  displayOrder: number;
  inUse: boolean;
  usageCount: number;
}

interface CustomFieldItemProps {
  field: TeamContactFieldSchema;
  onUpdate: () => void;
  onDelete: () => void;
}

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

export const CustomFieldItem: React.FC<CustomFieldItemProps> = ({ field, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fieldLabel, setFieldLabel] = useState(field.fieldLabel);
  const [fieldType, setFieldType] = useState(field.fieldType);

  const [updateField, { loading: updating }] = useMutation(UPDATE_TEAM_CONTACT_FIELD_SCHEMA);
  const [deleteField, { loading: deleting }] = useMutation(DELETE_TEAM_CONTACT_FIELD_SCHEMA);

  const handleSave = async () => {
    try {
      const { data } = await updateField({
        variables: {
          id: field.id,
          fieldLabel,
          fieldType,
        },
      });

      if (data?.updateTeamContactFieldSchema?.errors?.length > 0) {
        alert(data.updateTeamContactFieldSchema.errors.join('\n'));
        return;
      }

      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Failed to update field');
    }
  };

  const handleCancel = () => {
    setFieldLabel(field.fieldLabel);
    setFieldType(field.fieldType);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      const { data } = await deleteField({
        variables: { id: field.id },
      });

      if (!data?.deleteTeamContactFieldSchema?.success) {
        alert(data?.deleteTeamContactFieldSchema?.message || 'Failed to delete field');
        return;
      }

      setShowDeleteModal(false);
      onDelete();
    } catch (error) {
      console.error('Error deleting field:', error);
      alert('Failed to delete field');
    }
  };

  if (isEditing) {
    return (
      <div className={styles.fieldItem}>
        <Form.Group className="mb-3">
          <Form.Label>Field Label</Form.Label>
          <Form.Control
            type="text"
            value={fieldLabel}
            onChange={(e) => setFieldLabel(e.target.value)}
            placeholder="Enter field label"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Field Type</Form.Label>
          <Select
            options={FIELD_TYPE_OPTIONS}
            value={FIELD_TYPE_OPTIONS.find((o) => o.value === fieldType) || null}
            onChange={(option: any) => setFieldType(option ? option.value : 'text')}
            isDisabled={field.inUse}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
          />
          {field.inUse && (
            <Form.Text className="text-muted">
              Cannot change type while field is in use
            </Form.Text>
          )}
        </Form.Group>

        <div className={styles.editActions}>
          <Button variant="secondary" size="sm" onClick={handleCancel} disabled={updating}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={updating || !fieldLabel}>
            {updating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  const isFieldInUse = field.inUse || field.usageCount > 0;

  return (
    <>
      <div className={styles.fieldItem}>
        <div className={styles.fieldInfo}>
          <div className={styles.fieldHeader}>
            <span className={styles.fieldLabel}>{field.fieldLabel}</span>
            <div className={styles.fieldMeta}>
              <span className={styles.fieldTypePill}>{fieldType}</span>
            </div>
            <div className={styles.fieldActions}>
              <button
                className={styles.iconButton}
                onClick={() => setIsEditing(true)}
                data-qa="edit-custom-field"
                title="Edit field"
              >
                <PencilIcon className={styles.icon} />
              </button>
              <button
                className={styles.iconButton}
                onClick={() => !isFieldInUse && setShowDeleteModal(true)}
                data-qa="delete-custom-field"
                title={isFieldInUse ? "Cannot delete while field is in use" : "Delete field"}
                disabled={isFieldInUse}
                style={{ opacity: isFieldInUse ? 0.4 : 1, cursor: isFieldInUse ? 'not-allowed' : 'pointer' }}
              >
                <TrashIcon className={styles.icon} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmActionModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onClick={handleDelete}
        title={<span>Delete Custom Field</span>}
        message={`Are you sure you want to delete the "${field.fieldLabel}" field?`}
        action={<span>{deleting ? 'Deleting...' : 'Delete'}</span>}
      />
    </>
  );
};
